/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { NavigationService } from '@treasury/core/navigation';
import { Loan, LoanService } from '@treasury/domain/loans';
import { TmDetailedError } from '@treasury/domain/shared';
import { UsersService } from '@treasury/domain/users';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { editIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-list-header';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import { TmPaginationControls } from '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { ContextMenuItem } from '@vaadin/context-menu';
import '@vaadin/grid';
import { GridSelectedItemsChangedEvent } from '@vaadin/grid';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import { css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import '../../components/jhd-status';
import { getFormattedStatus, getStatusColorToken } from '../../components/jhd-status';
import '../../components/templates/treasury-status-column.template';

export enum LoanPaymentStatus {
    PendingApproval = 'PendingApproval',
    Approved = 'Approved',
    Rejected = 'Rejected',
    Cancelled = 'Cancelled',
    Expired = 'Expired',
    ApprovalRejected = 'ApprovalRejected',
}

export const isLoanPaymentCancelable = (loanPayment: Loan) => {
    return (
        loanPayment.status !== 'Cancelled' &&
        loanPayment.status !== 'Expired' &&
        loanPayment.status !== 'ApprovalRejected' &&
        loanPayment.status !== 'PendingApproval'
    );
};

export const isLoanPaymentEditable = (loanPayment: Loan) => {
    return (
        loanPayment.status === 'Failed' ||
        loanPayment.status === 'Scheduled' ||
        loanPayment.status === 'ApprovalRejected' ||
        loanPayment.status === 'Expired' ||
        (loanPayment.status === 'Submitted' &&
            new Date(loanPayment.transferDate || '').toDateString() === new Date().toDateString())
    );
};

export const tagName = 'loan-payments-table';
@customElement(tagName)
export class LoanPaymentsTable extends TmBaseComponent {
    @property({ type: Array })
    public loanPayments: Loan[] = [];

    @property()
    public loading = true;

    @property({ type: Boolean })
    public isDesktop = false;

    @InjectProperty()
    declare private usersService: UsersService;

    @InjectProperty()
    declare private readonly notificationService: NotificationService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private loanService: LoanService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @state()
    private filteredLoanPayments: Loan[] = [];

    @state()
    private selectedLoans: Loan[] = [];

    @state()
    private downloadOptions = [{ text: 'PDF' }];

    @state()
    private comments = '';

    @state()
    private filteredLoanPaymentsCount = 0;

    @state()
    private searchTerm = '';

    @query('tm-pagination-controls')
    private paginationControls!: TmPaginationControls;

    private get fieldsToFilter() {
        return this.isDesktop
            ? [
                  'transactionId',
                  'toAccount.accountDisplayLabel',
                  'status',
                  'paymentOptionType',
                  'amountFormatted',
                  'fromAccount.accountDisplayLabel',
                  'transferDateFormatted',
                  'createdDateFormatted',
              ]
            : [
                  'toAccount.accountDisplayLabel',
                  'status',
                  'amountFormatted',
                  'fromAccount.accountDisplayLabel',
                  'transferDateFormatted',
              ];
    }

    protected updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('loading') && !this.loading) {
            this.filteredLoanPayments = this.loanPayments;
            this.filterResults();
        }
    }

    protected render() {
        return html`<div class="flex flex-col h-full">
            <div class="flex-none">
                <tm-list-header
                    class=${classMap({
                        'bg-primary': !this.isDesktop,
                    })}
                    .filterButton=${true}
                    .searchTerm=${this.searchTerm}
                    @filterButtonClick=${() =>
                        this.dispatchEvent(new CustomEvent('filter-loan-payments'))}
                    @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
                >
                </tm-list-header>
            </div>
            <div class="flex-grow">${this.renderTable()}</div>
            <div class="flex-none">${this.renderPaginationControls()}</div>
        </div> `;
    }

    private loanClicked(loanPayment: Loan) {
        if (!loanPayment) {
            return;
        }

        const selectedLoan = this.loanPayments.find((lp: Loan) => lp.id === loanPayment.id);
        this.dispatchEvent(new CustomEvent('loan-clicked', { detail: selectedLoan }));
    }

    private filterResults(e?: CustomEvent) {
        let filteredLoanPayments = this.loanPayments;
        if (e) {
            this.searchTerm = e.detail.value;
        }
        filteredLoanPayments = filterTableResults(
            {
                detail: { value: this.searchTerm },
            } as CustomEvent,
            this.loanPayments,
            this.fieldsToFilter
        ) as [];
        this.filteredLoanPaymentsCount = filteredLoanPayments.length;
        if (this.isDesktop) {
            const offset = this.paginationControls?.calculateOffset();
            const limit = this.paginationControls?.pageSize;
            this.filteredLoanPayments = filteredLoanPayments.slice(offset, offset + limit);
        } else {
            this.filteredLoanPayments = filteredLoanPayments;
        }
    }

    private renderStatusIconColumn(loanPayment: Loan) {
        return html`<jhd-status .status=${loanPayment.status}></jhd-status>`;
    }

    private renderToAccountAndStatusColumn(loanPayment: Loan) {
        return html`<div class="flex -ml-4">
            <div class="flex flex-col">
                <span class="payment-name text-sm font-medium"
                    >${loanPayment.toAccount.accountDisplayLabel}</span
                >
                <small class="payment-transaction-id text-xs text-tertiary"
                    >From ${loanPayment.fromAccount.accountDisplayLabel}</small
                >
                <small
                    class="payment-status text-xs"
                    style=${`color: var(${getStatusColorToken(loanPayment.status)})`}
                    >${getFormattedStatus(loanPayment.status)}</small
                >
            </div>
        </div>`;
    }

    private renderAmountAndFromAccountColumn(loanPayment: Loan) {
        return html`<div class="flex flex-col">
            <span class="payment-amount text-right text-sm font-medium"
                >${loanPayment.amountFormatted}</span
            >
            <small class="payment-transaction-id text-right text-xs text-secondary overflow-scroll"
                >${loanPayment.transferDateFormatted}</small
            >
        </div>`;
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.filteredLoanPayments.length) {
            return html`<div
                class=${classMap({
                    'w-full px-4 py-8 bg-primary text-center text-sm text-secondary': true,
                    'shadow-md': !this.isDesktop,
                })}
            >
                No loan payments found
            </div>`;
        }
        const isSelectable = (item: Loan) => {
            return (
                item.status === 'PendingApproval' && item.permissions?.some(p => p === 'Approve')
            );
        };
        return html`<vaadin-grid
            class=${classMap({
                'h-full': true,
                'shadow-md': !this.isDesktop,
            })}
            .isItemSelectable=${isSelectable}
            .items=${this.filteredLoanPayments}
            @active-item-changed=${({ detail }: CustomEvent) => {
                this.loanClicked(detail.value);
            }}
            @selected-items-changed="${(e: GridSelectedItemsChangedEvent<Loan>) => {
                this.selectedLoans = e.detail.value;
                this.dispatchEvent(
                    new CustomEvent('selection-changed', {
                        detail: this.selectedLoans,
                    })
                );
            }}"
        >
            ${this.renderColumns()}</vaadin-grid
        >`;
    }

    private renderColumns() {
        if (this.isDesktop) {
            return html`<vaadin-grid-selection-column
                    frozen
                    width="3.5rem"
                ></vaadin-grid-selection-column>
                <vaadin-grid-sort-column
                    header="Transaction ID"
                    path="transactionId"
                    ${columnBodyRenderer(
                        (item: Loan) =>
                            html`<div class="flex justify-between items-center">
                                <span class="text-sm">${item.transactionId}</span>${item.isEdited
                                    ? html`<tm-icon .icon=${editIcon}></tm-icon>`
                                    : nothing}
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column
                ><vaadin-grid-sort-column
                    header="From Account"
                    path="fromAccount.accountDisplayLabel"
                    ${columnBodyRenderer(
                        (item: Loan) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${item.fromAccount.accountDisplayLabel}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column
                ><vaadin-grid-sort-column
                    header="To Account"
                    path="toAccount.accountDisplayLabel"
                    ${columnBodyRenderer(
                        (item: Loan) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${item.toAccount.accountDisplayLabel}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column
                ><vaadin-grid-sort-column
                    header="Payment Option"
                    path="paymentOptionType"
                    ${columnBodyRenderer(
                        (item: Loan) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${item.paymentOptionType}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Created Date"
                    path="createdDateFormatted"
                    ${columnBodyRenderer(
                        (item: Loan) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${item.createdDateFormatted}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column
                ><vaadin-grid-sort-column
                    header="Payment Date"
                    path="transferDateFormatted"
                    ${columnBodyRenderer(
                        (item: Loan) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${item.transferDateFormatted}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Status"
                    path="status"
                    ${columnBodyRenderer((loan: Loan) => {
                        return html`<treasury-status-column
                            .item=${loan}
                            .completedApproversFunction=${async () =>
                                this.usersService.fetchCompletedApprovers({
                                    approvalEntity: 'internalTransfer',
                                    productId: loan.id as number | undefined,
                                    updatedBy: parseInt(loan.updatedBy),
                                }) as unknown as string[]}
                            .eligibleApproversFunction=${async () =>
                                this.usersService.fetchAvailableApprovers({
                                    approvalEntity: 'internalTransfer',
                                    productId: loan.id as number | undefined,
                                    updatedBy: parseInt(loan.updatedBy),
                                }) as unknown as string[]}
                        ></treasury-status-column>`;
                    })}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Amount"
                    path="amount"
                    text-align="end"
                    ${columnBodyRenderer((loan: Loan) => {
                        return html`<div class="flex flex-col">
                            <span class="amount text-sm">${loan.amountFormatted}</span>
                        </div>`;
                    })}
                ></vaadin-grid-sort-column>
                <vaadin-grid-column
                    frozen-to-end
                    flex-grow="0"
                    width="3rem"
                    text-align="end"
                    @click=${(e: Event) => e.stopPropagation()}
                    ${columnBodyRenderer((payment: Loan) => {
                        const cancelable = isLoanPaymentCancelable(payment);
                        const editable = isLoanPaymentEditable(payment);
                        const actions: Array<ContextMenuItem> = [];
                        if (editable) {
                            actions.push({ text: 'Edit' });
                        }
                        if (cancelable) {
                            actions.push({ text: 'Cancel' });
                        }
                        if (editable || cancelable) {
                            return html`<tm-actions-column
                                .items=${actions}
                                @action-selected=${(e: CustomEvent) => {
                                    const action = e.detail.value.text;
                                    if (action === 'Edit') {
                                        this.navService.navigate(
                                            `/loans/payment/edit/${payment.id}`
                                        );
                                    }
                                    if (action === 'Cancel') {
                                        this.cancelLoanPayment(payment);
                                    }
                                }}
                            ></tm-actions-column>`;
                        }
                        return nothing;
                    })}
                ></vaadin-grid-column>`;
        }
        return html`<vaadin-grid-column
                flex-grow="0"
                width="3.5rem"
                ${columnBodyRenderer(
                    this.renderStatusIconColumn as GridColumnBodyLitRenderer<Loan>,
                    []
                )}
            ></vaadin-grid-column>
            <vaadin-grid-column
                flex-grow="1"
                auto-width
                ${columnBodyRenderer(
                    this.renderToAccountAndStatusColumn as GridColumnBodyLitRenderer<Loan>,
                    []
                )}
            ></vaadin-grid-column>
            <vaadin-grid-column
                flex-grow="1"
                ${columnBodyRenderer(
                    this.renderAmountAndFromAccountColumn as GridColumnBodyLitRenderer<Loan>,
                    []
                )}
            ></vaadin-grid-column>`;
    }

    private renderPaginationControls() {
        if (!this.isDesktop) {
            return nothing;
        }
        return html`<tm-pagination-controls
            .totalItemCount=${this.filteredLoanPaymentsCount}
            @page-changed=${() => {
                this.filterResults();
            }}
        ></tm-pagination-controls>`;
    }

    private async cancelLoanPayment(payment: Loan) {
        const { reason } = await this.dialogService.open(
            html`<div>Are you sure you want to cancel this loan?</div>
                <br /><tm-text-field
                    label="Comments"
                    @value-changed=${(e: CustomEvent) => {
                        const { value } = e.detail;
                        this.comments = value;
                    }}
                ></tm-text-field>`,
            'Cancel Loan Payment',
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Close',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                },
            }
        );

        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.loanService.cancelLoanPayment(payment.id as number, this.comments);
                this.dispatchEvent(new CustomEvent('loan-payment-canceled', { detail: payment }));
                this.notificationService.renderSuccess('Loan payment canceled successfully');
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error decisioning loan payment.');
                }
            } finally {
                this.loading = false;
            }
        }
    }

    public static get styles() {
        return [
            css`
                .payment-amount {
                    text-align: right;
                }
                .table-header,
                .empty-message {
                    color: var(--header-text-color);
                }
                button[disabled] {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                vaadin-grid::part(cell) {
                    --_lumo-grid-secondary-border-color: var(--border-color);
                }
                vaadin-grid::part(first-column-cell) {
                    border-top: none;
                }
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoanPaymentsTable;
    }
}
